import React, { useCallback, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import Alert from 'utils/packages/Alert';
import Animated from 'react-native-reanimated';
import { AppView, withAnimatedNavigationHeader } from 'design-system';
import Button from 'design-system/Button';
import StatusBar from 'design-system/StatusBar';
import { IconProfile, IconEmail, IconPhone } from 'design-system/icons';
import { SettingsSection } from 'design-system/SettingsSection';
import { SettingsItemNavigate } from 'design-system/SettingsItem';
import Spacer from 'design-system/Spacer';
import Text from 'design-system/Text';
import { SCROLL_INDICATOR_INSETS, rem } from 'design-system/values';
import { validateEmail } from 'utils/validation';
import useTrustedContacts from 'features/invest/hooks/useTrustedContacts';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { focus } from 'hooks/useAutoFocusInput';
import { isWeb } from '../../../constants';
const strings = {
    emptyPlaceholder: 'Add...',
    trustedContact: 'Trusted Contact',
    email: 'Email',
    phoneNumber: 'Phone number',
    firstName: 'First name',
    lastName: 'Last name',
    alertTitle: 'Hold on 💫',
    alertTrustedInvalidEmail: 'You need to specify a valid email for your trusted contact.',
    alertTrustedInvalidPhone: 'You need to specify a valid phone number for your trusted contact.',
    disclaimer1: 'A Trusted Contact is someone you authorise us to contact and disclose information about your account to address possible financial exploitation, confirm your contact information, health status or the identity of any legal guardian, executor, trustee, or holder of a power of attorney.',
    disclaimer2: 'A Trusted Contact is not a beneficiary. Collecting this information is purely for your protection and doesn’t change your experience with Emma.',
};
export const EditTrustedContactScreen = withAnimatedNavigationHeader(({ navigation, scrollHandler }) => {
    const [trustedContacts, update] = useTrustedContacts();
    const contact = trustedContacts ? trustedContacts[trustedContacts.length - 1] : undefined;
    const [state, setState] = useState({
        email: '',
        lastName: '',
        firstName: '',
        phoneNumber: '',
    });
    const [loading, setLoading] = useState(false);
    const editFirstName = useCallback(() => {
        navigation.navigateDeprecated('TextFieldPage', {
            title: strings.firstName,
            defaultValue: state.firstName || contact?.firstName,
            autoCapitalize: 'words',
            onPressNext: (value) => {
                const trimmedValue = value.trim();
                if (trimmedValue !== contact?.firstName) {
                    setState((oldState) => ({
                        ...oldState,
                        firstName: trimmedValue,
                    }));
                }
                else {
                    setState((oldState) => ({
                        ...oldState,
                        firstName: '',
                    }));
                }
                navigation.pop();
            },
        });
    }, [contact?.firstName, state.firstName]);
    const editLastName = useCallback(() => {
        navigation.navigateDeprecated('TextFieldPage', {
            title: strings.lastName,
            defaultValue: state.lastName || contact?.lastName,
            autoCapitalize: 'words',
            onPressNext: (value) => {
                const trimmedValue = value.trim();
                if (trimmedValue !== contact?.lastName) {
                    setState((oldState) => ({
                        ...oldState,
                        lastName: trimmedValue,
                    }));
                }
                else {
                    setState((oldState) => ({
                        ...oldState,
                        lastName: '',
                    }));
                }
                navigation.pop();
            },
        });
    }, [contact, state.lastName]);
    const editEmail = useCallback(() => {
        navigation.navigateDeprecated('TextFieldPage', {
            title: strings.email,
            defaultValue: state.email || contact?.email,
            keyboardType: 'email-address',
            autoCapitalize: 'none',
            onPressNext: (value, _, inputRef) => {
                const trimmedValue = value.trim();
                if (validateEmail(trimmedValue)) {
                    if (trimmedValue !== contact?.lastName) {
                        setState((oldState) => ({
                            ...oldState,
                            email: trimmedValue,
                        }));
                    }
                    else {
                        setState((oldState) => ({
                            ...oldState,
                            email: '',
                        }));
                    }
                    navigation.pop();
                }
                else {
                    Alert.alert(strings.alertTitle, strings.alertTrustedInvalidEmail, [
                        { text: 'Okay', onPress: () => focus(inputRef) },
                    ]);
                }
            },
        });
    }, [contact, state.email]);
    const editPhone = useCallback(() => {
        navigation.navigateDeprecated('PhoneFieldPage', {
            title: strings.phoneNumber,
            defaultValue: state.phoneNumber || contact?.phoneNumber,
            onPressNext: (value) => {
                if (value.replace(/\D/g, '').length < 11) {
                    Alert.alert(strings.alertTitle, strings.alertTrustedInvalidPhone);
                }
                else {
                    const phoneString = value.replace(/[^\d+]/g, '');
                    if (phoneString === contact?.phoneNumber) {
                        setState((oldState) => ({
                            ...oldState,
                            phoneNumber: '',
                        }));
                    }
                    else {
                        setState((oldState) => ({
                            ...oldState,
                            phoneNumber: value.replace(/[^\d+]/g, ''),
                        }));
                    }
                    navigation.pop();
                }
            },
        });
    }, [contact, state.phoneNumber]);
    const handleSave = useCallback(async () => {
        const { email, lastName, firstName, phoneNumber } = state;
        setLoading(true);
        try {
            await update({
                ...contact,
                ...(email && { email }),
                ...(lastName && { lastName }),
                ...(firstName && { firstName }),
                ...(phoneNumber && { phoneNumber: phoneNumber.replace(/[^\d+]/g, '') }),
            });
            navigation.pop();
        }
        catch {
            setLoading(false);
        }
    }, [contact, state, update]);
    const isButtonVisible = (() => {
        const { email, lastName, firstName, phoneNumber } = state;
        if (!!email || !!lastName || firstName || phoneNumber)
            return true;
        return false;
    })();
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    const paddingBottomWithBtn = useMarginBottom('paddingBottom', rem(56 + 16), paddingHorizontalStyle);
    return (<AppView type="secondary" withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={isButtonVisible ? paddingBottomWithBtn : paddingBottom}>
          <View style={styles.container}>
            <SettingsSection noMarginTop>
              <SettingsItemNavigate icon={<IconProfile gradientKey="ultimate"/>} description={strings.firstName} title={state.firstName || contact?.firstName || strings.emptyPlaceholder} onPress={editFirstName}/>
              <SettingsItemNavigate icon={<IconProfile gradientKey="skyBlue"/>} description={strings.lastName} title={state.lastName || contact?.lastName || strings.emptyPlaceholder} onPress={editLastName}/>
              <SettingsItemNavigate icon={<IconEmail />} description={strings.email} title={state.email || contact?.email || strings.emptyPlaceholder} onPress={editEmail}/>
              <SettingsItemNavigate icon={<IconPhone gradientKey="skyBlue"/>} description={strings.phoneNumber} title={state.phoneNumber || contact?.phoneNumber || strings.emptyPlaceholder} onPress={editPhone}/>
            </SettingsSection>
            <View style={styles.disclaimer}>
              <Text TextThin-14 Gray>
                {strings.disclaimer1}
              </Text>
              <Spacer h={16}/>
              <Text TextThin-14 Secondary>
                {strings.disclaimer2}
              </Text>
            </View>
          </View>
        </Animated.ScrollView>
        {isButtonVisible ? <Button brand title="Save" onPress={handleSave} floating loading={loading}/> : null}
      </AppView>);
}, ({ colors }) => ({
    title: 'Trusted contact',
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styles = StyleSheet.create({
    container: { flex: 1, paddingTop: rem(16) },
    disclaimer: { padding: rem(16) },
});
